import { useNavigate } from 'react-router-dom';

const WaterGasManagementPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Utility Network <span className="text-[#00E600]">Intelligence</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            Infrastructure doesn't fail suddenly. It whispers warnings weeks before catastrophe. AI hears what humans miss.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Overview</h2>
          <div className="space-y-6 text-lg text-[#B3B3B3] leading-relaxed">
            <p className="text-xl text-white font-semibold">
              A burst pipe wastes 100,000 gallons before human operators notice. A gas leak triggers evacuations 
              before sensors alarm. By the time you react, the damage compounds. Infrastructure protection isn't 
              about faster response—it's about prediction before problems exist.
            </p>
            <p>
              NYAXO's SimNet™ suite combines computational fluid dynamics (CFD), machine learning, and real-time 
              SCADA integration to model utility networks with sub-second accuracy. We detect micro-pressure anomalies 
              that signal leaks 48 hours before surface evidence. We predict equipment failures 2-3 weeks before 
              breakdowns. We optimize flow dynamics that human intuition can't process.
            </p>
            <p>
              Municipal systems serving 50M+ people. Industrial networks spanning continents. From natural gas 
              transmission to potable water distribution—NYAXO's AI protects infrastructure that can't afford failure.
            </p>
          </div>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Critical <span className="text-[#00E600]">Challenges</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Undetected Network Losses (NRW)</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Water utilities lose 30-50% of supply to leaks, costing $14B+ globally. By the time leaks surface, millions of gallons are wasted.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: AI detects micro-pressure signatures 48hrs before visible leaks, pinpointing location within 10m using acoustic correlation.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Infrastructure Aging & Failure</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> 240,000 water main breaks annually in US alone. Reactive maintenance costs 3-5x more than preventive action.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Predictive analytics forecast pipe failures 2-3 weeks ahead, prioritizing repairs before catastrophic breaks occur.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Energy Waste in Pumping Operations</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Pumping accounts for 80% of utility energy costs. Manual optimization misses 25-40% efficiency potential.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: ML algorithms optimize pump scheduling, VFD operations, and pressure zones, reducing energy consumption 30-45%.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300">
              <h3 className="text-xl font-bold text-white mb-4">Gas Network Safety Risks</h3>
              <p className="text-[#B3B3B3] mb-3">
                <span className="text-white font-semibold">Problem:</span> Gas leaks cause 300+ incidents/year in US, resulting in evacuations, explosions, and fatalities.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Solution: Real-time transient analysis detects pressure anomalies instantly, triggering automated isolation protocols within seconds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Software Solutions */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">
            Our <span className="text-[#00E600]">Software Solutions</span>
          </h2>
          <p className="text-lg text-[#B3B3B3] mb-12 leading-relaxed">
            NYAXO's SimNet™ suite represents the pinnacle of computational fluid dynamics (CFD) and network hydraulics. 
            Our AI-enhanced simulation platforms leverage advanced numerical methods, real-time SCADA integration, and 
            machine learning algorithms to deliver unprecedented accuracy in infrastructure modeling.
          </p>

          {/* Gas Management Software */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#00E600] mb-6">
              Gas Distribution Networks
            </h3>
            <div className="space-y-6">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet SSGas™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Steady-State Gas Network Simulation Engine</p>
                <p className="text-[#B3B3B3] mb-4">
                  Advanced hydraulic solver for natural gas distribution systems utilizing Newton-Raphson iterative methods 
                  and Weymouth/Panhandle equation frameworks. Handles complex network topologies with 10,000+ nodes, 
                  compressor stations, pressure-regulating valves (PRV), and multi-source injection points.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Hardy Cross algorithm optimization</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Multi-gas composition modeling</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">ISO 5167 compliance validation</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">GIS integration (ESRI/QGIS)</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet TSGas™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Transient Gas Flow Dynamics Simulator</p>
                <p className="text-[#B3B3B3] mb-4">
                  Real-time transient analysis platform leveraging partial differential equations (PDE) and method-of-characteristics 
                  (MOC) for dynamic gas flow modeling. Predicts pressure wave propagation, surge analysis, and compressor trip scenarios 
                  with millisecond-resolution accuracy. Critical for emergency response and operational planning.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Implicit finite difference schemes</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">AI-powered surge prediction</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Compressor performance curves</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">SCADA live-data integration</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Water Management Software */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#00E600] mb-6">
              Water Distribution Systems
            </h3>
            <div className="space-y-6">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet SSWater™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Hydraulic Network Analysis Platform</p>
                <p className="text-[#B3B3B3] mb-4">
                  Enterprise-grade potable water distribution solver implementing Hazen-Williams and Darcy-Weisbach friction models. 
                  Optimizes pump scheduling, analyzes water age, tracks chlorine residuals, and performs pressure zone management 
                  across municipal networks. EPA EPANET-compatible with enhanced AI-driven leak detection algorithms.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Gradient-based optimization (GA)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Water quality modeling (WQ)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">DMA (District Metered Area) analysis</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">ISO 24516 NRW compliance</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet TSWater™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Extended Period Simulation (EPS) Engine</p>
                <p className="text-[#B3B3B3] mb-4">
                  Time-dependent hydraulic and water quality simulator utilizing finite element analysis (FEA) for dynamic flow conditions. 
                  Models tank filling/draining cycles, pump start/stop sequences, PRV operations, and water age propagation over 24-168 hour 
                  simulation periods. Machine learning predicts demand patterns and optimizes energy consumption.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Adaptive time-stepping algorithms</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Pump efficiency curves (VFD)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Contaminant transport modeling</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Energy cost optimization (kWh)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sewerage Management Software */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#00E600] mb-6">
              Pressurized Sewerage Networks
            </h3>
            <div className="space-y-6">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet SSSewage™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Wastewater Pressure System Simulator</p>
                <p className="text-[#B3B3B3] mb-4">
                  Specialized hydraulic solver for pressurized sewage collection networks (STEP systems, grinder pump stations). 
                  Handles non-Newtonian fluid dynamics, solids transport, septage characteristics, and pump station sequencing. 
                  Compliant with EN 1091 standards for pressure sewerage design and optimizes pump selection for variable flow conditions.
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Manning's equation adaptation</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Grinder pump station modeling</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Solids settling velocity analysis</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Odor control system integration</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* District Heating Software */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-[#00E600] mb-6">
              District Heating Networks
            </h3>
            <div className="space-y-6">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300">
                <h4 className="text-xl font-bold text-white mb-3">SimNet SSHeat™</h4>
                <p className="text-sm text-[#00E600] mb-3 uppercase tracking-wide">Thermal Network Hydraulics Platform</p>
                <p className="text-[#B3B3B3] mb-4">
                  Advanced thermal-hydraulic solver for district heating (DH) and combined heat-power (CHP) distribution systems. 
                  Integrates heat loss calculations, temperature drop modeling, and pressure compensation across supply/return pipes. 
                  Optimizes substation differential pressure controllers (DPCV), handles variable temperature operation (VTO), and 
                  supports fourth-generation DH network design (4GDH).
                </p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Thermal transient analysis (TTA)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">Heat loss coefficient modeling</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">CHP plant integration</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#B3B3B3]">EN 13941 design compliance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Excellence Section */}
          <div className="bg-gradient-to-r from-[#0D0D0D] to-[#1A1A1A] border border-[#00E600] rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Technical Excellence</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">99.97%</div>
                <p className="text-white font-semibold mb-1">Computational Accuracy</p>
                <p className="text-[#B3B3B3]">Validated against field measurements</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">50,000+</div>
                <p className="text-white font-semibold mb-1">Node Capacity</p>
                <p className="text-[#B3B3B3]">Enterprise-scale network modeling</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">Real-Time</div>
                <p className="text-white font-semibold mb-1">SCADA Integration</p>
                <p className="text-[#B3B3B3]">Live operational monitoring</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Sectors We <span className="text-[#00E600]">Protect</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Municipal Water Utilities', challenge: 'Aging infrastructure & NRW', benefit: '35% leak reduction, $50M+ savings/year' },
              { name: 'Natural Gas Transmission', challenge: 'Safety compliance & efficiency', benefit: 'Zero incidents, 20% energy optimization' },
              { name: 'Industrial Process Water', challenge: 'Quality control & uptime', benefit: '99.9% availability, contamination prevention' },
              { name: 'District Heating Systems', challenge: 'Heat loss & balancing', benefit: '25% energy savings, optimal temperature distribution' },
              { name: 'Wastewater Collection', challenge: 'Pump station failures & odor', benefit: 'Predictive maintenance, 90% uptime improvement' },
              { name: 'Oil & Gas Midstream', challenge: 'Pipeline integrity & monitoring', benefit: 'Real-time leak detection, regulatory compliance' }
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-2"><span className="text-white font-semibold">Challenge:</span> {industry.challenge}</p>
                <p className="text-sm text-[#00E600] font-semibold">→ {industry.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYAXO */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Why <span className="text-[#00E600]">SimNet</span> Leads
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#0D0D0D] border-l-4 border-[#00E600] rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Technical Superiority</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">99.97% Computational Accuracy:</span> Validated against 10,000+ field measurements across 6 continents</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">50,000+ Node Capacity:</span> Enterprise-scale networks with sub-second solve times</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Real-Time SCADA Integration:</span> Live data ingestion, not batch processing</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">AI-Enhanced Modeling:</span> ML predicts anomalies traditional CFD can't detect</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Multi-Physics Simulation:</span> Hydraulic, thermal, and quality modeling in one platform</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Waste Reduction</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">35%</div>
                <p className="text-[#B3B3B3]">Average decrease in Non-Revenue Water (NRW)</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Early Detection</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">48hr</div>
                <p className="text-[#B3B3B3]">Leak prediction before visible surface evidence</p>
              </div>
              <div className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Network Uptime</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">99.8%</div>
                <p className="text-[#B3B3B3]">System availability with predictive maintenance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Protect Critical Infrastructure</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Infrastructure failure isn't an option. Let SimNet™ predict problems before they exist.
          </p>
          <button
            onClick={() => navigate('/#contact')}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Schedule Technical Demo
          </button>
        </div>
      </section>
    </div>
  );
};

export default WaterGasManagementPage;